import { DateTime, Settings } from 'luxon';
import i18n from 'i18next';


const ENV = (import.meta as any).env;
const browserLanguage = navigator.language.split('-')[0] || ENV.VITE_APP_LANGUAGE;




export const formatDate = (date: string, calendarDisplay: string) => {
  if (!date || date === '') { return ''; }

  let formattedDate = DateTime.fromJSDate(new Date(date)).toFormat('hh:mm');

  switch (calendarDisplay) {
    case 'day':
    case '':
      formattedDate = DateTime.fromJSDate(new Date(date)).toFormat('HH:mm');
      break;
    case 'week':
    case 'month':
      formattedDate = DateTime.fromJSDate(new Date(date)).toFormat('d/M');
      break;
    // formattedDate = moment(new Date(date)).format("MM");
    // break;
    case 'year':
      const dateObj = DateTime.fromJSDate(new Date(date));
      const monthIndex = dateObj.month - 1; // Luxon months are 1-indexed, array is 0-indexed
      const monthNamesShort = i18n.t('calendar.monthNamesShort', { returnObjects: true }) as string[];
      formattedDate = monthNamesShort[monthIndex] || dateObj.toFormat('MMM');
      break;
    case 'decade':
      formattedDate = DateTime.fromJSDate(new Date(date)).setLocale(browserLanguage).toFormat('MM-YYYY').replace(/^./, c => c.toUpperCase());
      break;
  }

  return (formattedDate.toString().startsWith('0') && formattedDate.length) ===
    2
    ? `${formattedDate[1]}:00`
    : formattedDate;
};

export const formatTimeStamp = (date: string, calendarDisplay: string) => {
  const formattedDate = DateTime.fromISO(date).setLocale(browserLanguage);

  let formattedTimeStamp = formattedDate.toFormat('HH:mm');

  switch (calendarDisplay) {
    case 'day':
      formattedTimeStamp = formattedDate.toFormat('HH:mm');
      break;
    case 'week':
    case 'month':
    case 'year':
      formattedTimeStamp = formattedDate.toFormat('DD-MM');
      break;
    // formattedDate = moment(new Date(date)).format("MM");
    // break;
    case 'decade':
      formattedTimeStamp = formattedDate.toFormat('MM-YYYY');
      break;
  }

  return formattedTimeStamp;
};

// ---- NEW UTILITY -------------------------------------------------------
// generateTickValues
// ------------------------------------------------------------------------
// Centralised helper to generate sensible, deterministic tick positions for
// the X-axis depending on the currently selected calendar view.
//
// The function expects the full list of ISO-8601 date strings that feed the
// chart together with the *time granularity* that is currently selected by
// the user.  It returns an array of epoch millisecond values that can be fed
// straight into the `tickValues` prop of VisXʼs `<AxisBottom />` component.
//
// • day   → ticks at 00:00, 06:00, 12:00, 18:00 and 24:00 (i.e. next-day 00:00)
// • week  → ticks at Monday, Wednesday, Friday and Sunday (00:00 of each day)
// • month → ticks at 1st, 7th, 14th, 21st and last day of the month
// • year  → ticks at Jan, Mar, May, Jul, Sep and Nov (1st of the month 00:00)
//
// NOTE: Luxon is used instead of native Date math to keep the implementation
// short and locale safe.  The helper purposely *does not* try to be perfect
// for every edge-case (e.g. incomplete datasets) – callers should filter the
// returned array against their scale domain if necessary.
// ------------------------------------------------------------------------
export const generateTickValues = (
  dates: string[] = [],
  calendarDisplay: 'day' | 'week' | 'month' | 'year'
): number[] => {
  if (dates.length === 0) return [];

  const first = DateTime.fromISO(dates[0]);

  switch (calendarDisplay) {
    case 'day': {
      const start = first.startOf('day');
      const hours = [0, 6, 12, 18, 24];
      return hours.map((h) => start.plus({ hours: h }).toMillis());
    }
    case 'week': {
      // Luxonʼs `.startOf('week')` respects locale.  We explicitly want Monday
      // as first day to stay consistent, therefore we normalise via `.set`.
      const monday = first.startOf('week').set({ weekday: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
      const offsets = [0, 2, 4, 6]; // Mon, Wed, Fri, Sun
      return offsets.map((d) => monday.plus({ days: d }).toMillis());
    }
    case 'month': {
      const firstDay = first.startOf('month');
      const lastDay = first.endOf('month').startOf('day');
      const offsets = [0, 6, 13, 20]; // 1st, 7th, 14th, 21st
      const ticks = offsets.map((d) => firstDay.plus({ days: d }));
      ticks.push(lastDay);
      return ticks.map((dt) => dt.toMillis());
    }
    case 'year': {
      const jan = first.startOf('year');
      const monthOffsets = [0, 2, 4, 6, 8, 10]; // Jan, Mar, May, Jul, Sep, Nov
      return monthOffsets.map((m) => jan.plus({ months: m }).toMillis());
    }
    default:
      return [];
  }
};
